import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { GravityToastProvider } from "@/components/effects/GravityToast";
import CursorTrail from "@/components/effects/CursorTrail";
import ScrollToTop from "@/components/effects/ScrollToTop";
import AmbientSound from "@/components/effects/AmbientSound";
import CookieConsent from "@/components/effects/CookieConsent";
import ReferralCapture from "@/components/effects/ReferralCapture";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Book from "@/pages/Book";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import Forum from "@/pages/Forum";
import ForumCategory from "@/pages/ForumCategory";
import ForumThread from "@/pages/ForumThread";
import Contact from "@/pages/Contact";
import Auth from "@/pages/Auth";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Checkout from "@/pages/Checkout";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Welcome from "@/pages/Welcome";
import SetPassword from "@/pages/SetPassword";
import NotFound from "@/pages/NotFound";
import OrderTracking from "@/pages/OrderTracking";
// The whole admin console (plus its heavy editor/upload deps) is lazy-loaded
// so visitors never download it as part of the main bundle.
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminJournal = lazy(() => import("@/pages/admin/AdminJournal"));
const AdminForum = lazy(() => import("@/pages/admin/AdminForum"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminComments = lazy(() => import("@/pages/admin/AdminComments"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminBookPreview = lazy(() => import("@/pages/admin/AdminBookPreview"));
const AdminShipping = lazy(() => import("@/pages/admin/AdminShipping"));
const AdminBookAvailability = lazy(() => import("@/pages/admin/AdminBookAvailability"));
const AdminCourses = lazy(() => import("@/pages/admin/AdminCourses"));
const AdminEvents = lazy(() => import("@/pages/admin/AdminEvents"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminCampaigns = lazy(() => import("@/pages/admin/AdminCampaigns"));
const AdminPayPalTest = lazy(() => import("@/pages/admin/AdminPayPalTest"));
const AdminDelivery = lazy(() => import("@/pages/admin/AdminDelivery"));
const AdminSubscribers = lazy(() => import("@/pages/admin/AdminSubscribers"));
const AdminBundle = lazy(() => import("@/pages/admin/AdminBundle"));
const AdminQRCodes = lazy(() => import("@/pages/admin/AdminQRCodes"));

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <GravityToastProvider>
            <ScrollToTop />
            <ReferralCapture />
            <CursorTrail />
            <AmbientSound />
            <CookieConsent />
            <div className="min-h-screen flex flex-col">
              <Routes>
                <Route path="/admin" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-sacred-gold/30 border-t-sacred-gold rounded-full animate-spin" /></div>}>
                    <AdminLayout />
                  </Suspense>
                }>
                  <Route index element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="journal" element={<AdminJournal />} />
                  <Route path="forum" element={<AdminForum />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="comments" element={<AdminComments />} />
                  <Route path="contacts" element={<AdminContacts />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="book-preview" element={<AdminBookPreview />} />
                  <Route path="shipping" element={<AdminShipping />} />
                  <Route path="book-availability" element={<AdminBookAvailability />} />
                  <Route path="courses" element={<AdminCourses />} />
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="campaigns" element={<AdminCampaigns />} />
                  <Route path="paypal" element={<AdminPayPalTest />} />
                  <Route path="delivery" element={<AdminDelivery />} />
                  <Route path="subscribers" element={<AdminSubscribers />} />
                  <Route path="bundle" element={<AdminBundle />} />
                  <Route path="qr-codes" element={<AdminQRCodes />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
                {/* Welcome page - hidden from nav, no Navbar/Footer */}
                <Route path="/welcome" element={<Welcome />} />
                <Route path="*" element={
                  <>
                    <Navbar />
                    <main className="flex-1">
                      <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/book" element={<Book />} />
                        <Route path="/courses" element={<Courses />} />
                        <Route path="/courses/:slug" element={<CourseDetail />} />
                        <Route path="/events" element={<Events />} />
                        <Route path="/events/:slug" element={<EventDetail />} />
                        <Route path="/journal" element={<Journal />} />
                        <Route path="/journal/:slug" element={<JournalPost />} />
                        <Route path="/forum" element={<Forum />} />
                        <Route path="/forum/:slug" element={<ForumCategory />} />
                        <Route path="/forum/thread/:id" element={<ForumThread />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/auth/magic" element={<Auth />} />
                        <Route path="/auth/set-password" element={<SetPassword />} />
                        <Route path="/profile/:username" element={<Profile />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/track-order" element={<OrderTracking />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </main>
                    <Footer />
                  </>
                } />
              </Routes>
            </div>
          </GravityToastProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
